import { useEffect, useMemo, useRef, useState } from "react";
import type { Provider } from "../types";

export interface ModelChoice {
  providerID: string;
  modelID: string;
}

interface FlatModel {
  key: string;
  providerID: string;
  providerName: string;
  modelID: string;
  label: string;
}

interface Group {
  providerID: string;
  providerName: string;
  models: FlatModel[];
}

const MAX_RESULTS = 120;

/**
 * Seletor de modelo em dois níveis (combobox):
 *   1) a busca localiza o GRUPO (provedor);
 *   2) ao entrar no grupo, escolhe-se o modelo (com busca própria).
 * Navegação por teclado: setas, Enter (entrar/selecionar), Esc (voltar/fechar).
 * Renderiza no máximo MAX_RESULTS linhas por nível para manter o desempenho
 * com catálogos grandes (8.000+ modelos).
 */
export function ModelSelect({
  providers,
  value,
  onChange,
  allowDefault = true,
  id,
  ariaLabel,
  placement = "auto",
  connected,
}: {
  providers: Provider[];
  value: ModelChoice | null;
  onChange: (m: ModelChoice | null) => void;
  /** Se true, oferece a opção "Modelo padrão" (value = null). */
  allowDefault?: boolean;
  id?: string;
  ariaLabel?: string;
  /** Direção de abertura: "auto" escolhe pelo espaço disponível. */
  placement?: "auto" | "up" | "down";
  /** IDs de provedores já conectados (com credencial). Sem chave, exibe aviso no grupo. */
  connected?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [groupId, setGroupId] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Catálogo achatado (para rótulo do gatilho e modelos por grupo).
  const flat = useMemo<FlatModel[]>(() => {
    const out: FlatModel[] = [];
    for (const p of providers) {
      for (const m of Object.values(p.models ?? {})) {
        out.push({
          key: `${p.id}/${m.id}`,
          providerID: p.id,
          providerName: p.name,
          modelID: m.id,
          label: m.name ?? m.id,
        });
      }
    }
    return out;
  }, [providers]);

  // Nível 1: grupos (provedores).
  const groupsAll = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    for (const f of flat) {
      const g = map.get(f.providerID);
      if (g) g.models.push(f);
      else map.set(f.providerID, { providerID: f.providerID, providerName: f.providerName, models: [f] });
    }
    return [...map.values()];
  }, [flat]);

  const q = query.trim().toLowerCase();
  const inGroup = groupId !== null;

  // Nível 1 filtrado: a busca é sobre o GRUPO (nome/id do provedor).
  const groupsShown = useMemo(() => {
    const list = inGroup
      ? []
      : q
        ? groupsAll.filter((g) => g.providerName.toLowerCase().includes(q) || g.providerID.toLowerCase().includes(q))
        : groupsAll;
    return list.slice(0, MAX_RESULTS);
  }, [groupsAll, q, inGroup]);
  const groupsHidden = (inGroup ? 0 : (q ? groupsAll.filter((g) => g.providerName.toLowerCase().includes(q) || g.providerID.toLowerCase().includes(q)).length : groupsAll.length)) - groupsShown.length;

  // Nível 2 filtrado: modelos DENTRO do grupo escolhido.
  const modelsShown = useMemo(() => {
    if (!inGroup) return [];
    const g = groupsAll.find((x) => x.providerID === groupId);
    if (!g) return [];
    const list = q ? g.models.filter((f) => f.label.toLowerCase().includes(q) || f.modelID.toLowerCase().includes(q)) : g.models;
    return list.slice(0, MAX_RESULTS);
  }, [groupsAll, groupId, q, inGroup]);
  const modelsHidden = (() => {
    if (!inGroup) return 0;
    const g = groupsAll.find((x) => x.providerID === groupId);
    if (!g) return 0;
    const total = q ? g.models.filter((f) => f.label.toLowerCase().includes(q) || f.modelID.toLowerCase().includes(q)).length : g.models.length;
    return total - modelsShown.length;
  })();

  const currentGroup = inGroup ? groupsAll.find((x) => x.providerID === groupId) : null;

  const currentLabel = value
    ? flat.find((f) => f.key === `${value.providerID}/${value.modelID}`)?.label ??
      `${value.providerID}/${value.modelID}`
    : "Modelo padrão";

  // Linhas navegáveis por teclado no nível atual.
  const rows = useMemo(() => {
    if (inGroup) return modelsShown.map((f) => ({ kind: "model" as const, f }));
    const out: Array<{ kind: "default" } | { kind: "group"; g: Group }> = [];
    if (allowDefault) out.push({ kind: "default" });
    for (const g of groupsShown) out.push({ kind: "group", g });
    return out;
  }, [inGroup, modelsShown, groupsShown, allowDefault]);

  // Posiciona o painel com coordenadas fixas para não ser cortado por
  // contêineres com overflow (ex.: corpo de modal).
  const place = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(360, window.innerWidth * 0.85);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const desired = 380;
    const openDown =
      placement === "down" ||
      (placement === "auto" && (spaceBelow >= desired || spaceBelow >= spaceAbove));
    const space = Math.max(120, openDown ? spaceBelow : spaceAbove);
    setPos(
      openDown
        ? { left, top: rect.bottom + 6, maxHeight: space }
        : { left, bottom: window.innerHeight - rect.top + 6, maxHeight: space },
    );
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onScrollOrResize = (e: Event) => {
      // Ignora scroll dentro do próprio painel; fecha em qualquer outro.
      if (e.type === "scroll" && rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setGroupId(null);
      setActive(0);
      place();
      // Foca o campo de busca quando o painel abre.
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const commit = (f: FlatModel | null) => {
    onChange(f ? { providerID: f.providerID, modelID: f.modelID } : null);
    setOpen(false);
  };

  const enterGroup = (g: Group) => {
    setGroupId(g.providerID);
    setQuery("");
    setActive(0);
    inputRef.current?.focus();
  };

  const goBack = () => {
    setGroupId(null);
    setQuery("");
    setActive(0);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Impede que o Escape continue borbulhando e feche o modal por baixo.
      e.stopPropagation();
      if (inGroup) goBack();
      else setOpen(false);
      return;
    }
    const max = rows.length - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, max));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (!row) return;
      if (row.kind === "default") commit(null);
      else if (row.kind === "group") enterGroup(row.g);
      else commit(row.f);
    }
  };

  return (
    <div className="model-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className="select model-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? "Modelo"}
        onClick={() => {
          place();
          setOpen((o) => !o);
        }}
      >
        <span className="model-select-label">{currentLabel}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open ? (
        <div
          className="model-select-panel"
          style={{ left: pos?.left ?? 0, top: pos?.top, bottom: pos?.bottom, maxHeight: pos?.maxHeight }}
          onKeyDown={onKeyDown}
          onMouseDown={(e) => {
            // Mantém o foco no campo de busca ao clicar em linhas/áreas do
            // painel (o Escape precisa continuar sendo capturado aqui).
            const t = e.target as HTMLElement;
            if (t.tagName === "INPUT" || t.closest("button")) return;
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          {inGroup ? (
            <div className="model-select-head">
              <button type="button" className="model-select-back" onClick={goBack} aria-label="Voltar aos provedores">
                ‹
              </button>
              <span className="model-select-head-title">{currentGroup?.providerName}</span>
              <span className="model-select-head-count">{currentGroup?.models.length} modelo(s)</span>
            </div>
          ) : null}
          <input
            ref={inputRef}
            className="input"
            type="search"
            placeholder={
              inGroup
                ? `Pesquisar modelo em ${currentGroup?.providerName ?? ""}…`
                : "Pesquisar provedor (grupo)…"
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            aria-label={inGroup ? "Pesquisar modelo no grupo" : "Pesquisar provedor"}
          />
          <div className="model-select-list" ref={listRef} role="listbox">
            {!inGroup && allowDefault ? (
              <div
                className={`model-select-item ${active === 0 ? "active" : ""}`}
                data-idx={0}
                role="option"
                aria-selected={value === null}
                onMouseEnter={() => setActive(0)}
                onClick={() => commit(null)}
              >
                <span className="model-select-name">Modelo padrão</span>
                <span className="model-select-sub">usar a configuração do OpenCode</span>
              </div>
            ) : null}

            {!inGroup
              ? groupsShown.map((g, i) => {
                  const idx = i + (allowDefault ? 1 : 0);
                  const selected = value?.providerID === g.providerID;
                  return (
                    <div
                      key={g.providerID}
                      className={`model-select-item model-select-group-row ${active === idx ? "active" : ""} ${selected ? "selected" : ""}`}
                      data-idx={idx}
                      role="option"
                      aria-selected={selected}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => enterGroup(g)}
                    >
                      <span className="model-select-name">
                        {g.providerName}
                        {selected ? " ✓" : ""}
                      </span>
                      <span className="model-select-sub">
                        {g.providerID} · {g.models.length} modelo(s){" "}
                        {(providers.find((x) => x.id === g.providerID)?.env?.length ?? 0) > 0 &&
                        !(connected ?? []).includes(g.providerID) ? (
                          <strong title="Este provedor exige uma chave de API; você será solicitado ao escolher um modelo.">
                            · 🔑 requer chave
                          </strong>
                        ) : null}{" "}
                        ›
                      </span>
                    </div>
                  );
                })
              : modelsShown.map((f, idx) => {
                  const selected = value && `${value.providerID}/${value.modelID}` === f.key;
                  return (
                    <div
                      key={f.key}
                      className={`model-select-item ${active === idx ? "active" : ""} ${selected ? "selected" : ""}`}
                      data-idx={idx}
                      role="option"
                      aria-selected={!!selected}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => commit(f)}
                    >
                      <span className="model-select-name">{f.label}</span>
                      <span className="model-select-sub">{f.modelID}</span>
                    </div>
                  );
                })}

            {(inGroup ? modelsShown.length : groupsShown.length) === 0 ? (
              <div className="model-select-empty">
                Nenhum {inGroup ? "modelo" : "provedor"} encontrado para “{query}”.
              </div>
            ) : null}
            {(inGroup ? modelsHidden : groupsHidden) > 0 ? (
              <div className="model-select-empty">
                +{inGroup ? modelsHidden : groupsHidden} resultado(s) — refine a busca para ver todos.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
